<template>
  <div class="detect-main">
    <section class="detect-hero">
      <h2>AIGC 检测</h2>
      <p>
        基于自然语言处理与深度学习模型，逐段分析文档内容是否可能由 AI 生成。
        检测结果反映 AI 生成概率，与文本质量无关，仅供学术自查参考。
      </p>
    </section>

    <div class="detect-workspace">
      <div class="upload-panel">
        <div class="panel-head">
          <h2>上传需要检测的文件</h2>
          <p class="panel-tip">
            按段落智能检测正文内容，自动跳过标题、题注等无关段落；检测过程约 2–5 分钟/万字，请耐心等待。
          </p>
        </div>

        <div
            class="upload-zone"
            :class="{ dragover: dragOver, uploading: uploading, readonly: readonly }"
            @click="pickFile"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop">
          <input
              ref="fileInput"
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              class="file-input"
              :disabled="readonly || uploading"
              @change="onFileChange">
          <div v-if="uploading" class="upload-loading">
            <div class="loading-spinner"></div>
            <p class="upload-loading-title">正在上传并解析文档…</p>
            <p v-if="uploadingFileName" class="upload-loading-name">{{ uploadingFileName }}</p>
            <p class="upload-loading-hint">请勿关闭页面，解析完成后即可提交检测</p>
          </div>
          <template v-else>
            <i class="bi bi-plus-lg upload-icon"></i>
            <p class="upload-title">将原文件拖到此处，或 <span class="upload-link">点击上传</span></p>
            <p class="upload-hint">每个文件限制 10MB 以内，仅支持 docx 格式，最多上传 1 个文件</p>
          </template>
        </div>

        <div class="form-fields">
          <div class="form-row">
            <label class="form-label">标题</label>
            <div class="form-control">
              <input
                  v-model="docTitle"
                  type="text"
                  maxlength="50"
                  placeholder="请输入准确的文章标题（选填）"
                  :disabled="readonly">
              <span class="char-count">{{ docTitle.length }}/50</span>
            </div>
          </div>
          <div class="form-row">
            <label class="form-label">作者</label>
            <div class="form-control">
              <input
                  v-model="docAuthor"
                  type="text"
                  maxlength="50"
                  placeholder="请填写作者信息，多位作者用「;」间隔（选填）"
                  :disabled="readonly">
              <span class="char-count">{{ docAuthor.length }}/50</span>
            </div>
          </div>
        </div>

        <div v-if="uploadSession" class="upload-info">
          <div class="file-row">
            <i class="bi bi-file-earmark-word"></i>
            <div class="file-meta">
              <div class="file-name">{{ uploadSession.sourceFileName }}</div>
              <div class="file-sub">
                检测 {{ uploadSession.detectableCount }} 段 · 跳过 {{ uploadSession.skippedCount }} 段 ·
                预计 {{ uploadSession.quotaCost || 0 }} 点
              </div>
            </div>
            <button type="button" class="link-btn" @click="removeUpload">移除</button>
          </div>
          <div class="submit-row">
            <button type="button" class="primary-btn" :disabled="!canSubmit" @click="handleSubmit">
              {{ submitting ? '提交中…' : '提交检测' }}
            </button>
          </div>
          <p v-if="balanceInsufficient" class="warn-text">可用点数不足，请先充值</p>
        </div>

        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>

        <p class="legal-note">
          请遵守学术规范与相关法律法规，检测结果仅供自查参考，不得用于伪造或篡改检测报告等不当行为。
        </p>
      </div>

      <aside class="info-panel">
        <div class="notice-box">
          <div class="notice-head">
            <i class="bi bi-exclamation-circle-fill"></i>
            <span>检测服务提示</span>
          </div>
          <p>
            本服务为自研AIGC检测引擎，结果反映 AI 生成可能性，与学校指定平台（如知网、维普等）无直接对应关系，请以官方要求为准。
          </p>
        </div>

        <ul class="feature-list">
          <li v-for="item in features" :key="item.title" class="feature-item">
            <div class="feature-icon" :class="item.iconClass">
              <i :class="item.icon"></i>
            </div>
            <div class="feature-body">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </div>
          </li>
        </ul>
      </aside>
    </div>

    <div class="table-panel">
      <div class="table-head">
        <h3>检测记录</h3>
        <button type="button" class="ghost-btn" :disabled="recordsLoading" @click="loadRecords">
          <i class="bi bi-arrow-clockwise" :class="{ spinning: recordsLoading }"></i>
        </button>
      </div>

      <div v-if="!readonly && records.length" class="check-report-table">
        <div v-for="item in records" :key="item.taskNo" class="table-row">
          <div class="item version">
            <div class="item-label">版本</div>
            <div class="item-value"><span class="tag tag-free">标准版</span></div>
          </div>
          <div class="item title">
            <div class="item-label">文档标题</div>
            <div class="item-value title-text">{{ displayRecordTitle(item) }}</div>
          </div>
          <div class="item status">
            <div class="item-label">状态</div>
            <div class="item-value">
              <span v-if="isProcessing(item)" class="tag tag-loading">
                检测中<span class="dot"></span><span class="dot"></span><span class="dot"></span>
              </span>
              <span v-else-if="item.status === '2'" class="tag tag-done">已完成</span>
              <span v-else-if="item.status === '3'" class="tag tag-fail" :title="item.errorMsg">失败</span>
              <span v-else class="tag tag-queue">排队中</span>
            </div>
          </div>
          <div class="item rate">
            <div class="item-label">AIGC总体疑似度</div>
            <div class="item-value rate-text">
              {{ item.status === '2' && item.overallAiRate != null ? formatRate(item.overallAiRate) + '%' : '-' }}
            </div>
          </div>
          <div class="item date">
            <div class="item-label">提交日期</div>
            <div class="item-value">{{ item.createTime || '-' }}</div>
          </div>
          <div class="item operate">
            <div class="item-label">操作</div>
            <div class="item-value op-links">
              <button
                  type="button"
                  class="op-link"
                  :disabled="item.status !== '2'"
                  @click="viewReport(item)">
                <i class="bi bi-file-text"></i>查看报告
              </button>
              <button
                  type="button"
                  class="op-link"
                  :disabled="item.status !== '2' || downloadingTaskNo === item.taskNo"
                  @click="downloadReport(item)">
                <i class="bi bi-download"></i>{{ downloadingTaskNo === item.taskNo ? '下载中…' : '下载报告' }}
              </button>
              <button type="button" class="op-link danger" @click="deleteRecord(item)">
                <i class="bi bi-trash"></i>删除
              </button>
            </div>
          </div>
        </div>
      </div>

      <p v-else-if="readonly" class="empty-text">登录后查看检测记录</p>
      <p v-else-if="recordsLoading" class="empty-text">加载中…</p>
      <p v-else class="empty-text">暂无检测记录，上传文档后开始检测</p>

      <p class="table-tip">检测完成后请尽快下载离线报告。失败任务将显示具体原因，可重新上传提交新任务。</p>
    </div>
  </div>
</template>

<script>
import {
  uploadDetectDocx,
  deleteDetectUpload,
  submitDetect,
  getDetectTasks,
  deleteDetectRecord,
  downloadDetectReport
} from '@/api/portal/detect'

const POLL_INTERVAL = 3000

function buildRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'dt-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10)
}

export default {
  name: 'DetectView',
  props: {
    readonly: { type: Boolean, default: false },
    balance: { type: Number, default: 0 }
  },
  data() {
    return {
      dragOver: false,
      uploading: false,
      submitting: false,
      uploadingFileName: '',
      uploadSession: null,
      docTitle: '',
      docAuthor: '',
      errorMessage: '',
      pollTimer: null,
      records: [],
      recordsLoading: false,
      downloadingTaskNo: '',
      features: [
        {
          icon: 'bi bi-cpu',
          iconClass: 'icon-tech',
          title: '先进的检测技术',
          desc: '采用 NLP 与深度学习模型，经大量文本样本训练，精准识别 AI 生成特征。'
        },
        {
          icon: 'bi bi-list-check',
          iconClass: 'icon-para',
          title: '段落级精细检测',
          desc: '逐段分析正文内容，自动跳过标题与题注，高亮标注疑似 AI 段落。'
        },
        {
          icon: 'bi bi-bar-chart-line',
          iconClass: 'icon-report',
          title: '可视化检测报告',
          desc: '生成详细检测报告，展示全文疑似率与各段落 AI 分值，支持在线查看与下载。'
        },
        {
          icon: 'bi bi-shield-lock',
          iconClass: 'icon-safe',
          title: '可靠的安全保障',
          desc: '全程加密传输与存储，严格保护科研隐私，检测完成后可自主管理记录。'
        }
      ]
    }
  },
  computed: {
    canSubmit() {
      return !this.readonly && !this.submitting && !this.uploading && this.uploadSession && !this.balanceInsufficient
    },
    balanceInsufficient() {
      if (!this.uploadSession || !this.uploadSession.quotaCost) {
        return false
      }
      return this.balance < this.uploadSession.quotaCost
    }
  },
  mounted() {
    if (!this.readonly) {
      this.loadRecords()
      this.startPolling()
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    displayRecordTitle(item) {
      if (!item) {
        return '-'
      }
      return item.docTitle || item.sourceFileName || item.taskNo
    },
    formatRate(val) {
      const num = Number(val)
      return Number.isFinite(num) ? num.toFixed(2) : '--'
    },
    isProcessing(item) {
      return item && (item.status === '0' || item.status === '1')
    },
    hasActiveRecords() {
      return this.records.some(item => this.isProcessing(item))
    },
    pickFile() {
      if (this.readonly || this.uploading) {
        if (this.readonly) {
          this.$emit('require-login')
        }
        return
      }
      this.$refs.fileInput && this.$refs.fileInput.click()
    },
    onDragOver() {
      if (!this.readonly && !this.uploading) {
        this.dragOver = true
      }
    },
    onDragLeave() {
      this.dragOver = false
    },
    onDrop(e) {
      this.dragOver = false
      if (this.readonly || this.uploading) {
        if (this.readonly) {
          this.$emit('require-login')
        }
        return
      }
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
      if (file) {
        this.uploadFile(file)
      }
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0]
      if (file) {
        this.uploadFile(file)
      }
      e.target.value = ''
    },
    uploadFile(file) {
      if (!file.name.toLowerCase().endsWith('.docx')) {
        this.errorMessage = '仅支持 .docx 格式文件'
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        this.errorMessage = '文件大小不能超过 10MB'
        return
      }
      this.errorMessage = ''
      this.uploading = true
      this.uploadingFileName = file.name
      uploadDetectDocx(file)
          .then(res => {
            this.uploadSession = res.data
          })
          .catch(err => {
            this.errorMessage = err.message || '上传失败'
          })
          .finally(() => {
            this.uploading = false
            this.uploadingFileName = ''
          })
    },
    removeUpload() {
      if (!this.uploadSession || !this.uploadSession.uploadId) {
        this.uploadSession = null
        return
      }
      deleteDetectUpload(this.uploadSession.uploadId)
          .then(() => {
            this.uploadSession = null
          })
          .catch(err => {
            this.errorMessage = err.message || '删除失败'
          })
    },
    handleSubmit() {
      if (this.readonly) {
        this.$emit('require-login')
        return
      }
      if (!this.uploadSession || this.balanceInsufficient) {
        if (this.balanceInsufficient) {
          this.$emit('require-recharge')
        }
        return
      }
      this.submitting = true
      this.errorMessage = ''
      const payload = {
        uploadId: this.uploadSession.uploadId,
        requestId: buildRequestId()
      }
      const title = this.docTitle.trim()
      const author = this.docAuthor.trim()
      if (title) {
        payload.title = title
      }
      if (author) {
        payload.author = author
      }
      submitDetect(payload)
          .then(() => {
            this.uploadSession = null
            this.docTitle = ''
            this.docAuthor = ''
            this.$emit('detect-submitted')
            this.loadRecords()
            this.startPolling()
          })
          .catch(err => {
            this.errorMessage = err.message || '提交失败'
          })
          .finally(() => {
            this.submitting = false
          })
    },
    loadRecords() {
      this.recordsLoading = true
      getDetectTasks({ page: 1, pageSize: 50 })
          .then(res => {
            this.records = res.data || []
            if (!this.hasActiveRecords()) {
              this.stopPolling()
            } else if (!this.pollTimer) {
              this.startPolling()
            }
          })
          .catch(() => {
            this.records = []
          })
          .finally(() => {
            this.recordsLoading = false
          })
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(() => {
        if (this.hasActiveRecords()) {
          this.loadRecords()
        } else {
          this.stopPolling()
        }
      }, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    viewReport(item) {
      if (!item || item.status !== '2') {
        return
      }
      const route = this.$router.resolve({
        path: '/detect/report',
        query: { taskId: item.taskNo }
      })
      window.open(route.href, '_blank')
    },
    downloadReport(item) {
      if (!item || item.status !== '2') {
        return
      }
      this.downloadingTaskNo = item.taskNo
      downloadDetectReport(item.taskNo)
          .then(blob => {
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `aigc-report-${item.taskNo}.html`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
          })
          .catch(err => {
            this.errorMessage = err.message || '下载失败'
          })
          .finally(() => {
            this.downloadingTaskNo = ''
          })
    },
    deleteRecord(item) {
      if (!item || !item.taskNo) {
        return
      }
      if (!window.confirm('确定删除该检测记录吗？')) {
        return
      }
      deleteDetectRecord(item.taskNo)
          .then(() => this.loadRecords())
          .catch(err => {
            this.errorMessage = err.message || '删除失败'
          })
    }
  }
}
</script>

<style scoped>
.detect-main {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detect-hero {
  text-align: center;
  padding: 8px 16px 4px;
}

.detect-hero h1 {
  margin: 0 0 10px;
  font-size: 28px;
  font-weight: 700;
  color: #111827;
}

.detect-hero p {
  margin: 0 auto;
  max-width: 720px;
  font-size: 14px;
  line-height: 1.75;
  color: #6b7280;
}

.detect-workspace {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(260px, 1fr);
  gap: 20px;
  align-items: stretch;
}

.upload-panel,
.table-panel,
.info-panel {
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.upload-panel {
  display: flex;
  flex-direction: column;
  padding: 22px 24px 20px;
}

.panel-head h2 {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
}

.panel-tip {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #9ca3af;
}

.upload-zone {
  margin-top: 16px;
  padding: 36px 20px;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  background: #fafafa;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.upload-zone:not(.readonly):hover,
.upload-zone.dragover {
  border-color: #1976d2;
  background: #e3f2fd;
}

.upload-zone.readonly {
  cursor: not-allowed;
  opacity: 0.7;
}

.upload-zone.uploading {
  pointer-events: none;
  border-color: #1976d2;
  background: #e3f2fd;
  cursor: wait;
}

.upload-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #1976d2;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.upload-loading-title {
  margin: 4px 0 0;
  font-size: 15px;
  font-weight: 600;
  color: #1565c0;
}

.upload-loading-name {
  margin: 0;
  max-width: 100%;
  font-size: 13px;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-loading-hint {
  margin: 0;
  font-size: 12px;
  color: #9ca3af;
}

.file-input { display: none; }

.upload-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  font-size: 22px;
  color: #1976d2;
}

.upload-title {
  margin: 12px 0 6px;
  font-size: 15px;
  color: #374151;
}

.upload-link {
  color: #1976d2;
  font-weight: 500;
}

.upload-hint {
  margin: 0;
  font-size: 12px;
  color: #9ca3af;
}

.form-fields {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.form-label {
  flex-shrink: 0;
  width: 48px;
  font-size: 14px;
  color: #374151;
}

.form-control {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.form-control input {
  flex: 1;
  min-width: 0;
  padding: 9px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.form-control input:focus {
  border-color: #1976d2;
}

.form-control input:disabled {
  background: #f9fafb;
  cursor: not-allowed;
}

.char-count {
  flex-shrink: 0;
  font-size: 12px;
  color: #9ca3af;
}

.upload-info {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed #e5e7eb;
}

.file-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-row > i {
  font-size: 26px;
  color: #2563eb;
}

.file-meta { flex: 1; min-width: 0; }

.file-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-sub {
  margin-top: 4px;
  font-size: 13px;
  color: #6b7280;
}

.link-btn {
  border: none;
  background: none;
  color: #1976d2;
  font-size: 14px;
  cursor: pointer;
}

.submit-row { margin-top: 12px; }

.primary-btn {
  padding: 10px 28px;
  border: none;
  border-radius: 999px;
  background: var(--primary, #1976d2);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.primary-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.warn-text,
.error-text {
  margin: 10px 0 0;
  font-size: 14px;
  color: #b91c1c;
}

.legal-note {
  margin: auto 0 0;
  padding-top: 14px;
  border-top: 1px solid #f3f4f6;
  font-size: 12px;
  line-height: 1.65;
  color: #9ca3af;
}

.info-panel {
  display: flex;
  flex-direction: column;
  padding: 20px;
}

.notice-box {
  padding: 14px 16px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.notice-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #b91c1c;
}

.notice-head i {
  font-size: 16px;
}

.notice-box p {
  margin: 0;
  font-size: 13px;
  line-height: 1.65;
  color: #7f1d1d;
}

.feature-list {
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.feature-item {
  display: flex;
  gap: 14px;
}

.feature-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 20px;
  color: #fff;
}

.feature-icon.icon-tech { background: linear-gradient(135deg, #1976d2, #1565c0); }
.feature-icon.icon-para { background: linear-gradient(135deg, #0ea5e9, #0284c7); }
.feature-icon.icon-report { background: linear-gradient(135deg, #42a5f5, #1976d2); }
.feature-icon.icon-safe { background: linear-gradient(135deg, #10b981, #059669); }

.feature-body h3 {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.feature-body p {
  margin: 0;
  font-size: 12px;
  line-height: 1.65;
  color: #6b7280;
}

.table-panel {
  padding: 20px 24px;
}

.table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.table-head h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.check-report-table {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.table-row {
  display: grid;
  grid-template-columns: 90px 1.6fr 110px 120px 160px 1.4fr;
  gap: 10px;
  padding: 14px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafafa;
}

.item-label {
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 6px;
}

.item-value {
  font-size: 14px;
  color: #111827;
}

.title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rate-text {
  font-size: 16px;
  font-weight: 600;
  color: #1976d2;
}

.tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
}

.tag-free {
  background: #e3f2fd;
  color: #1565c0;
}

.tag-loading {
  background: #fef3c7;
  color: #b45309;
}

.tag-done {
  background: #dcfce7;
  color: #15803d;
}

.tag-fail {
  background: #fef2f2;
  color: #b91c1c;
  cursor: help;
}

.tag-queue {
  background: #f3f4f6;
  color: #6b7280;
}

.dot {
  display: inline-block;
  width: 4px;
  height: 4px;
  margin-left: 3px;
  border-radius: 50%;
  background: currentColor;
  animation: blink 1.2s infinite;
}

.dot:nth-child(2) { animation-delay: 0.2s; }
.dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes blink {
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
}

.op-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.op-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  color: #1976d2;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
}

.op-link:disabled {
  color: #c4c4c4;
  cursor: not-allowed;
}

.op-link.danger {
  color: #ef4444;
}

.table-tip {
  margin: 14px 0 0;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.6;
}

.empty-text {
  margin: 0;
  padding: 24px 0;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
}

.ghost-btn {
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 18px;
  cursor: pointer;
}

.spinning {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 960px) {
  .detect-workspace {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1100px) {
  .table-row {
    grid-template-columns: 1fr 1fr;
  }

  .item.operate {
    grid-column: 1 / -1;
  }
}
</style>
